import { APIStatus } from '@application/common/enums/api-status';
import { createSlice } from '@reduxjs/toolkit';
import { createProjectTaskMappingAsync, getHourTypesAsync, getInternalDepartmentsAsync, getInternalDepartmentMembershipsAsync, getInternalBookingPermissionsAsync, getProjectTaskMappingsAsync, updateHourTypeDisplayNameAsync, updateInternalDepartmentAsync } from './hourRoutingThunk';
const initialState = {
    hourTypes: [],
    internalDepartments: [],
    projectTaskMappings: [],
    internalDepartmentMemberships: [],
    internalBookingPermissions: [],
    permissionsStatus: APIStatus.IDLE,
    hourTypesStatus: APIStatus.IDLE,
    internalDepartmentsStatus: APIStatus.IDLE,
    projectTaskMappingsStatus: APIStatus.IDLE,
    membershipsStatus: APIStatus.IDLE,
    mutationStatus: APIStatus.IDLE
};
const replaceById = (items, replacement) => items.map((item) => (item.id === replacement.id ? replacement : item));
const isSameProjectTaskMappingQuery = (left, right) => left?.targetType === right.targetType && left.targetId === right.targetId;
export const hourRoutingSlice = createSlice({
    name: 'hourRouting',
    initialState,
    reducers: {
        clearHourRoutingMutationError(state) {
            state.mutationError = undefined;
            if (state.mutationStatus === APIStatus.FAILED)
                state.mutationStatus = APIStatus.IDLE;
        },
        clearProjectTaskMappings(state) {
            state.projectTaskMappings = [];
            state.projectTaskMappingQuery = undefined;
            state.projectTaskMappingsRequestId = undefined;
            state.projectTaskMappingsError = undefined;
            state.projectTaskMappingsStatus = APIStatus.IDLE;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getInternalBookingPermissionsAsync.pending, (state, action) => {
            if (state.permissionsFreelancerId !== action.meta.arg)
                state.internalBookingPermissions = [];
            state.permissionsFreelancerId = action.meta.arg;
            state.permissionsRequestId = action.meta.requestId;
            state.permissionsStatus = APIStatus.PENDING;
            state.permissionsError = undefined;
        })
            .addCase(getInternalBookingPermissionsAsync.fulfilled, (state, action) => {
            if (state.permissionsFreelancerId !== action.meta.arg || state.permissionsRequestId !== action.meta.requestId)
                return;
            state.internalBookingPermissions = action.payload;
            state.permissionsStatus = APIStatus.SUCCESS;
        })
            .addCase(getInternalBookingPermissionsAsync.rejected, (state, action) => {
            if (state.permissionsFreelancerId !== action.meta.arg || state.permissionsRequestId !== action.meta.requestId)
                return;
            state.permissionsStatus = APIStatus.FAILED;
            state.permissionsError = action.payload;
        });
        builder
            .addCase(getHourTypesAsync.pending, (state) => {
            state.hourTypesStatus = APIStatus.PENDING;
            state.hourTypesError = undefined;
        })
            .addCase(getHourTypesAsync.fulfilled, (state, action) => {
            state.hourTypesStatus = APIStatus.SUCCESS;
            state.hourTypes = action.payload;
        })
            .addCase(getHourTypesAsync.rejected, (state, action) => {
            state.hourTypesStatus = APIStatus.FAILED;
            state.hourTypesError = action.payload;
        })
            .addCase(getInternalDepartmentsAsync.pending, (state) => {
            state.internalDepartmentsStatus = APIStatus.PENDING;
            state.internalDepartmentsError = undefined;
        })
            .addCase(getInternalDepartmentsAsync.fulfilled, (state, action) => {
            state.internalDepartmentsStatus = APIStatus.SUCCESS;
            state.internalDepartments = action.payload;
        })
            .addCase(getInternalDepartmentsAsync.rejected, (state, action) => {
            state.internalDepartmentsStatus = APIStatus.FAILED;
            state.internalDepartmentsError = action.payload;
        })
            .addCase(getProjectTaskMappingsAsync.pending, (state, action) => {
            if (!isSameProjectTaskMappingQuery(state.projectTaskMappingQuery, action.meta.arg)) {
                state.projectTaskMappings = [];
            }
            state.projectTaskMappingsStatus = APIStatus.PENDING;
            state.projectTaskMappingsError = undefined;
            state.projectTaskMappingQuery = action.meta.arg;
            state.projectTaskMappingsRequestId = action.meta.requestId;
        })
            .addCase(getProjectTaskMappingsAsync.fulfilled, (state, action) => {
            if (state.projectTaskMappingsRequestId !== action.meta.requestId ||
                !isSameProjectTaskMappingQuery(state.projectTaskMappingQuery, action.meta.arg)) {
                return;
            }
            state.projectTaskMappingsStatus = APIStatus.SUCCESS;
            state.projectTaskMappings = action.payload;
        })
            .addCase(getProjectTaskMappingsAsync.rejected, (state, action) => {
            if (state.projectTaskMappingsRequestId !== action.meta.requestId ||
                !isSameProjectTaskMappingQuery(state.projectTaskMappingQuery, action.meta.arg)) {
                return;
            }
            state.projectTaskMappingsStatus = APIStatus.FAILED;
            state.projectTaskMappingsError = action.payload;
        })
            .addCase(getInternalDepartmentMembershipsAsync.pending, (state, action) => {
            if (state.membershipFreelancerId !== action.meta.arg) {
                state.internalDepartmentMemberships = [];
            }
            state.membershipsStatus = APIStatus.PENDING;
            state.membershipsError = undefined;
            state.membershipFreelancerId = action.meta.arg;
            state.membershipsRequestId = action.meta.requestId;
        })
            .addCase(getInternalDepartmentMembershipsAsync.fulfilled, (state, action) => {
            if (state.membershipsRequestId !== action.meta.requestId ||
                state.membershipFreelancerId !== action.meta.arg) {
                return;
            }
            state.membershipsStatus = APIStatus.SUCCESS;
            state.internalDepartmentMemberships = action.payload;
        })
            .addCase(getInternalDepartmentMembershipsAsync.rejected, (state, action) => {
            if (state.membershipsRequestId !== action.meta.requestId ||
                state.membershipFreelancerId !== action.meta.arg) {
                return;
            }
            state.membershipsStatus = APIStatus.FAILED;
            state.membershipsError = action.payload;
        });
        builder
            .addCase(updateHourTypeDisplayNameAsync.pending, (state) => {
            state.mutationStatus = APIStatus.PENDING;
            state.mutationError = undefined;
        })
            .addCase(updateHourTypeDisplayNameAsync.fulfilled, (state, action) => {
            state.mutationStatus = APIStatus.SUCCESS;
            state.hourTypes = replaceById(state.hourTypes, action.payload);
        })
            .addCase(updateHourTypeDisplayNameAsync.rejected, (state, action) => {
            state.mutationStatus = APIStatus.FAILED;
            state.mutationError = action.payload;
        })
            .addCase(updateInternalDepartmentAsync.pending, (state) => {
            state.mutationStatus = APIStatus.PENDING;
            state.mutationError = undefined;
        })
            .addCase(updateInternalDepartmentAsync.fulfilled, (state, action) => {
            state.mutationStatus = APIStatus.SUCCESS;
            state.internalDepartments = state.internalDepartments.map((department) => {
                if (department.id === action.payload.id)
                    return action.payload;
                if (action.payload.isDefaultForNewContracts && department.isDefaultForNewContracts) {
                    return { ...department, isDefaultForNewContracts: false };
                }
                return department;
            });
        })
            .addCase(updateInternalDepartmentAsync.rejected, (state, action) => {
            state.mutationStatus = APIStatus.FAILED;
            state.mutationError = action.payload;
        })
            .addCase(createProjectTaskMappingAsync.pending, (state) => {
            state.mutationStatus = APIStatus.PENDING;
            state.mutationError = undefined;
        })
            .addCase(createProjectTaskMappingAsync.fulfilled, (state, action) => {
            state.mutationStatus = APIStatus.SUCCESS;
            if (state.projectTaskMappingQuery?.targetType === action.payload.targetType &&
                state.projectTaskMappingQuery.targetId === action.payload.targetId) {
                state.projectTaskMappings.push(action.payload);
            }
        })
            .addCase(createProjectTaskMappingAsync.rejected, (state, action) => {
            state.mutationStatus = APIStatus.FAILED;
            state.mutationError = action.payload;
        });
    }
});
export const { clearHourRoutingMutationError, clearProjectTaskMappings } = hourRoutingSlice.actions;
export default hourRoutingSlice.reducer;
